import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      setFormError('Enter your username and password.');
      document.getElementById(!username ? 'username' : 'password')?.focus();
      return;
    }
    setFormError('');

    setIsLoading(true);

    try {
      const result = await login(username, password);

      if (result.success) {
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        setFormError(result.error || 'Sign in failed. Check your username and password.');
        document.getElementById('username')?.focus();
      }
    } catch (error) {
      setFormError('Something went wrong. Check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:flex-1 bg-wrg-blue-500 items-center justify-center p-12">
        <div className="max-w-md text-center">
          <h1 className="mb-4 flex justify-center">
            <img
              src="/WRG_LOGO_ALT.png"
              alt="WRG Travel logo"
              className="h-auto w-full max-w-xs"
              width="1000"
              height="205"
            />
          </h1>
          <p className="text-xl text-blue-200 mb-8">
            Travel Request System
          </p>
          <div className="space-y-4 text-left text-blue-100">
            <div className="flex items-start gap-3">
              <div className="size-8 rounded-full bg-blue-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-semibold">1</span>
              </div>
              <div>
                <p className="font-medium text-white">Submit Travel Requests</p>
                <p className="text-sm">Request flights, hotels, and ground transportation</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="size-8 rounded-full bg-blue-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-semibold">2</span>
              </div>
              <div>
                <p className="font-medium text-white">Get Approval</p>
                <p className="text-sm">Your travel lead reviews and approves requests</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="size-8 rounded-full bg-blue-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-semibold">3</span>
              </div>
              <div>
                <p className="font-medium text-white">Travel Coordinated</p>
                <p className="text-sm">Our travel team books everything for you</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <h1 className="flex justify-center">
              <img
                src="/WRG_LOGO_ALT.png"
                alt="WRG Travel logo"
                className="h-auto w-full max-w-[260px]"
                width="1000"
                height="205"
              />
            </h1>
            <p className="text-gray-600 mt-2">Travel Request System</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Sign In</h2>
            <p className="text-gray-600 mb-6">Enter your credentials to access the system</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username or email
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wrg-blue-500 focus:border-transparent transition-colors"
                  autoComplete="username"
                  required
                  aria-invalid={formError ? 'true' : undefined}
                  aria-describedby={formError ? 'login-error' : undefined}
                  disabled={isLoading}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-wrg-blue-500 hover:text-wrg-blue-600"
                  >
                    Forgot your password?
                  </Link>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wrg-blue-500 focus:border-transparent transition-colors"
                  autoComplete="current-password"
                  required
                  aria-invalid={formError ? 'true' : undefined}
                  aria-describedby={formError ? 'login-error' : undefined}
                  disabled={isLoading}
                />
              </div>

              {formError && (
                <p id="login-error" role="alert" className="text-sm text-error-600">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 px-4 bg-wrg-blue-500 text-white font-medium rounded-lg hover:bg-wrg-blue-700 focus:ring-4 focus:ring-wrg-blue-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin size-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in…
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-gray-500">
              <p>Having trouble signing in?</p>
              <p>Contact IT Support for assistance</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
